import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { DisplayInput } from "@/components/Atoms/DisplayField";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator/use-field-feedback-map";
import { SubOrganizationStatusField } from "@/features/sub-organizations/SubOrganizationStatusField";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useEntityActionAllowed } from "@/hooks/use-entity-action-allowed";
import {
	formatPhoneNumber,
	handlePhoneInputChange,
	phoneValueForStorage,
} from "@/lib/dynamicForms/utils/phoneFormatter.util";
import {
	type SubOrganizationDisplay,
	type UpdateSubOrganizationContactInfo,
	organizationQueryOptions,
	useUpdateSubOrganization,
} from "@/services/organization";
import { formatId } from "@/utils/format-id";
import { normalizeUSState, isValidUSState } from "@/utils/us-states";
import { isValidUSZipcode } from "@/utils/us-zipcode";
import { showApiErrorToast } from "@/utils/toast.utils";
import { SearchSchema } from "../$subOrgId";

const EditableField = {
	Name: "name",
	Street: "street1",
	City: "city",
	State: "state",
	PostalCode: "postalCode",
	Phone: "phone",
} as const;

type EditableField = (typeof EditableField)[keyof typeof EditableField];

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId/details",
)({
	component: RouteComponent,
	validateSearch: (search) => {
		return SearchSchema.parse(search);
	},
});

function contactPayload(
	subOrganization: SubOrganizationDisplay,
	patch: Partial<Record<EditableField, string>>,
): UpdateSubOrganizationContactInfo {
	const address = subOrganization.address;
	const value = (field: EditableField, current: string | null | undefined) =>
		patch[field] !== undefined ? patch[field] || null : current || null;

	return {
		name: value(EditableField.Name, subOrganization.name),
		phone: value(EditableField.Phone, subOrganization.phone),
		address: {
			street1: value(EditableField.Street, address?.street1),
			street2: address?.street2 || null,
			city: value(EditableField.City, address?.city),
			state: value(EditableField.State, address?.state),
			country: address?.country || null,
			postalCode: value(EditableField.PostalCode, address?.postalCode),
		},
	};
}

function RouteComponent() {
	const { t } = useAppTranslation();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/sub-organizations/(sub-orgs)/$subOrgId/details",
	});
	const subOrganizationQuery = useQuery(
		organizationQueryOptions.subOrganizations.detail(
			params.orgId,
			params.subOrgId,
		),
	);
	const subOrganization = subOrganizationQuery.data;
	const canUpdate = useEntityActionAllowed({
		name: "UpdateSubOrganization",
		entityType: "SubOrganization",
		entityId: params.subOrgId,
		enabled: params.subOrgId.length > 0,
	});
	const updateSubOrganization = useUpdateSubOrganization();
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<EditableField>();
	const [editingField, setEditingField] = useState<EditableField | null>(null);
	const [draftByField, setDraftByField] = useState<
		Partial<Record<EditableField, string>>
	>({});
	const [errorByField, setErrorByField] = useState<
		Partial<Record<EditableField, string>>
	>({});

	const showEdit = canUpdate.allowed;
	const labels: Record<EditableField, string> = {
		name: t("SubOrganization.Name", { useFieldInfo: true }),
		street1: t("Address.Street1", { useFieldInfo: true }),
		city: t("Address.City", { useFieldInfo: true }),
		state: t("Address.State", { useFieldInfo: true }),
		postalCode: t("Address.PostalCode", { useFieldInfo: true }),
		phone: t("SubOrganization.Phone", { useFieldInfo: true }),
	};

	const serverValue = (field: EditableField): string => {
		if (!subOrganization) return "";
		if (field === EditableField.Name) return subOrganization.name ?? "";
		if (field === EditableField.Phone) return subOrganization.phone ?? "";
		if (field === EditableField.Street)
			return subOrganization.address?.street1 ?? "";
		if (field === EditableField.City) return subOrganization.address?.city ?? "";
		if (field === EditableField.State)
			return subOrganization.address?.state ?? "";
		return subOrganization.address?.postalCode ?? "";
	};

	const displayValue = (field: EditableField): string => {
		const value = serverValue(field);
		if (field === EditableField.Phone) return formatPhoneNumber(value);
		return value;
	};

	const beginEdit = (field: EditableField) => {
		setErrorByField((prev) => ({ ...prev, [field]: undefined }));
		setEditingField(field);
		setDraftByField((prev) => ({ ...prev, [field]: displayValue(field) }));
	};

	const cancelEdit = (field: EditableField) => {
		setErrorByField((prev) => ({ ...prev, [field]: undefined }));
		setFieldFeedback(field, DisplayFieldFeedback.Idle);
		setDraftByField((prev) => {
			const next = { ...prev };
			delete next[field];
			return next;
		});
		setEditingField((current) => (current === field ? null : current));
	};

	const commit = async (field: EditableField) => {
		const raw = draftByField[field];
		if (raw === undefined || !subOrganization?.id) {
			setEditingField((current) => (current === field ? null : current));
			return;
		}

		let next = raw.trim();
		if (field === EditableField.Phone) next = phoneValueForStorage(raw);
		if (field === EditableField.State && next)
			next = normalizeUSState(next) || next;

		if (next === serverValue(field)) {
			cancelEdit(field);
			return;
		}

		if (field === EditableField.Name && !next) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldRequired", { field: labels.name }),
			}));
			return;
		}
		if (field === EditableField.Name && next.length > 50) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldLessThanLength", {
					field: labels.name,
					count: 50,
				}),
			}));
			return;
		}
		if (field === EditableField.Street && next.length > 100) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldLessThanLength", {
					field: labels.street1,
					count: 100,
				}),
			}));
			return;
		}
		if (field === EditableField.City && next.length > 50) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.fieldLessThanLength", {
					field: labels.city,
					count: 50,
				}),
			}));
			return;
		}
		if (field === EditableField.State && next && !isValidUSState(next)) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.usStateInvalid"),
			}));
			return;
		}
		if (field === EditableField.PostalCode && next && !isValidUSZipcode(next)) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.zipcodeInvalid"),
			}));
			return;
		}
		if (
			field === EditableField.Phone &&
			next &&
			!/^\d{10}$/.test(next)
		) {
			setErrorByField((prev) => ({
				...prev,
				[field]: t("common.validation.phoneOptionalTenDigits"),
			}));
			return;
		}

		setEditingField((current) => (current === field ? null : current));
		setFieldFeedback(field, DisplayFieldFeedback.Saving);
		try {
			await updateSubOrganization.mutateAsync({
				organizationId: params.orgId,
				subOrganizationId: subOrganization.id,
				data: contactPayload(subOrganization, { [field]: next }),
			});
			setErrorByField((prev) => ({ ...prev, [field]: undefined }));
			setDraftByField((prev) => {
				const cleared = { ...prev };
				delete cleared[field];
				return cleared;
			});
			setFieldFeedback(field, DisplayFieldFeedback.Saved);
		} catch (error) {
			setFieldFeedback(field, DisplayFieldFeedback.Idle);
			showApiErrorToast({
				error,
				message: t("org-admin.sub-organizations.details.saveFailed"),
			});
		}
	};

	const field = (key: EditableField) => (
		<DisplayInput
			key={key}
			label={labels[key]}
			value={displayValue(key)}
			state={
				editingField === key
					? DisplayFieldState.Active
					: errorByField[key]
						? DisplayFieldState.Error
						: DisplayFieldState.Enabled
			}
			showEdit={showEdit && editingField !== key}
			editAriaLabel={labels[key]}
			editValue={
				key === EditableField.Phone
					? (draftByField.phone ?? displayValue(key))
					: draftByField[key]
			}
			feedback={feedbackByField[key] ?? DisplayFieldFeedback.Idle}
			hintText={errorByField[key]}
			showHintText={Boolean(errorByField[key])}
			onEdit={() => beginEdit(key)}
			onEditChange={(value) =>
				setDraftByField((prev) => ({
					...prev,
					[key]:
						key === EditableField.Phone
							? handlePhoneInputChange(value)
							: value,
				}))
			}
			onEditCommit={() => {
				void commit(key);
			}}
			onEditCancel={() => cancelEdit(key)}
			data-testid={`sub-org-${key}`}
		/>
	);

	const count = (label: string, value: number | undefined) => (
		<DisplayInput
			label={label}
			value={value ?? 0}
			showEdit={false}
			data-testid={`sub-org-${label}`}
		/>
	);

	return (
		<div className="flex w-full flex-col gap-(--spacing-xs,4px)">
			{field(EditableField.Name)}
			<SubOrganizationStatusField
				organizationId={params.orgId}
				subOrganization={subOrganization}
			/>
			{field(EditableField.Street)}
			{field(EditableField.City)}
			{field(EditableField.State)}
			{field(EditableField.PostalCode)}
			{field(EditableField.Phone)}
			{count(
				t("org-admin.sub-organizations.tabs.details.userCount"),
				subOrganization?.totalAssignedUserCount,
			)}
			{count(
				t("org-admin.sub-organizations.tabs.details.allDataUserCount"),
				subOrganization?.usersWithFullDataAccessCount,
			)}
			{count(
				t("org-admin.sub-organizations.table.columns.assignedDataUserCount"),
				subOrganization?.usersWithLimitedDataAccessCount,
			)}
			<DisplayInput
				label={t("org-admin.sub-organizations.tabs.details.subOrganizationId")}
				value={formatId(subOrganization?.id)}
				copyable
				copyText={subOrganization?.id ?? ""}
				showEdit={false}
				copyAriaLabel={t("common.actions.copy")}
				data-testid="sub-org-id"
			/>
		</div>
	);
}
